import { ChangeDetectorRef, Component, HostListener, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { DialogConfirm, DialogConfirmData } from 'app/dialogs/confirm/dialog-confirm';
import {
    DialogMortgageEligibilityAddPropertyComponent,
    DialogMortgageEligibilityAddPropertyData,
} from 'app/dialogs/loans/mortgage-eligibility/add-property/dialog-mortgage-eligibility-add-property';
import {
    DialogMortgageEligibilityConfirmFetchPropertyInfoComponent,
    DialogMortgageEligibilityConfirmFetchPropertyInfoData,
} from 'app/dialogs/loans/mortgage-eligibility/confirm-fetch-property-info/dialog-mortgage-eligibility-confirm-fetch-property-info';
import {
    DialogMortgageEligibilityCopyDataComponent,
    DialogMortgageEligibilityCopyDataData,
} from 'app/dialogs/loans/mortgage-eligibility/copy-data/dialog-mortgage-eligibility-copy-data';
import {
    DialogMortgageEligibilityMissingOriginatingApplicationComponent,
    DialogMortgageEligibilityMissingOriginatingApplicationData,
} from 'app/dialogs/loans/mortgage-eligibility/missing-originating-application/dialog-mortgage-eligibility-missing-originating-application';
import { TranslocoLocaleWrapperService } from 'app/fuse-template/core/transloco/translocoLocale.service';
import { ComponentCanDeactivate } from 'app/helpers/pendingChangesGuard';
import { formatAddress } from 'app/helpers/stringHelpers';
import { ValidatePropertyNumber } from 'app/helpers/validators';
import { ExposedUpdateMortgageEligibilityInfoReq } from 'app/interfaces/requests/Exposed/exposedUpdateMortgageEligibilityInfoReq';
import { ExposedUpdateMortgageEligibilityPropertyInfoReq } from 'app/interfaces/requests/Exposed/exposedUpdateMortgageEligibilityPropertyInfoReq';
import { ExposedReadMortgageEligibilityConfigRes } from 'app/interfaces/responses/Exposed/exposedReadMortgageEligibilityConfigRes';
import {
    ExposedListMortgageEligibilityInfoPropertiesRes,
    ExposedMortgageEligibilityInfoOriginatingSystemMismatchRes,
    ExposedReadMortgageEligibilityInfoRes,
} from 'app/interfaces/responses/Exposed/exposedReadMortgageEligibilityInfoRes';
import { MortgageEligibilityInfoService } from 'app/services/api/mortgageEligibilityInfo/mortgageEligibilitInfo.service';
import { TranslationService } from 'app/services/api/translation/translation.service';
import { FormArrayTableOptions, FormArrayTableRowMoveEvent } from 'common/components/form-array-table/form-array-table.component';
import { PendingChangesDialogType } from 'common/constants/dialog-type.constants';
import { ORIGINATING_SYSTEMS } from 'common/constants/mortgage-eligibility-originating-systems.constants';
import { EMPTY, Observable, Subject, catchError, filter, finalize, map, of, switchMap, takeUntil, tap } from 'rxjs';

@Component({
    selector: 'mortgage-eligibility-form',
    templateUrl: './loans-mortgage-eligibility-form.component.html',
    styleUrls: ['./loans-mortgage-eligibility-form.component.scss'],
})
export class LoansMortgageEligibilityFormComponent implements OnInit, ComponentCanDeactivate {
    protected response: ExposedReadMortgageEligibilityInfoRes;
    protected currentMortgagesDisplayedColumns: string[] = ['mortgageLender', 'mortgageBalance', 'mortgageDeposit', 'mortgageNewBalance'];
    protected newMortgagesDisplayedColumns: string[] = ['mortgageType', 'mortgageBalance'];
    protected propertiesDisplayedColumns: string[] = [
        'propertyNumber',
        'address',
        'propertyEvaluation',
        'landEvaluation',
        'fireCompensationEvaluation',
    ];
    protected currentMortgagesArray: FormArray;
    protected newMortgagesArray: FormArray;
    protected properties: FormArray;
    protected config: ExposedReadMortgageEligibilityConfigRes;
    protected infoPanelMode: string = 'side';
    protected infoPanelOpen: boolean = true;
    protected currentDate: Date = new Date();
    private unsubscribeAll: Subject<any> = new Subject<void>();

    private isCheckingOriginatingSystem: boolean = false;

    pendingChangesDialogType: PendingChangesDialogType = 'save';
    beforeLeaveMessage = this.translationService.translate('info_leave_form_message');

    protected mortgageTableOptions: FormArrayTableOptions = {
        includeRowNumbers: true,
        allowRowDeletion: true,
        allowRowReorder: true,
        includeFooterRow: true,
    };

    protected propertiesTableOptions: FormArrayTableOptions = {
        includeRowNumbers: false,
        allowRowDeletion: true,
        allowRowReorder: false,
        allowRowRefresh: true,
    };

    protected form = this.formBuilder.group({
        externalServiceId: new FormControl(''),
        externalServiceLink: new FormControl(''),
        purchasePrice: new FormControl(''),
    });

    protected info = {
        mortgageRatios: {
            lsr: 0,
            other: 0,
            total: 0,
        },
        mortgageAmounts: {
            lsr: 0,
            other: 0,
            total: 0,
        },
        mortgageLimits: {
            onlyLSRLoans: {
                amount: 0,
                active: false,
                limitBasis: 'purchase_price',
            },
            withOtherLoans: {
                amount: 0,
                active: false,
                limitBasis: 'purchase_price',
            },
            fireCompensationAndLandEvaluation: { amount: 0, active: false },
            maxLimit: { amount: 0, active: false },
        },
        evaluationTotals: {
            propertyEvaluation: 0,
            landEvaluation: 0,
            fireCompensationEvaluation: 0,
        },
        maxAvailableMortgageAmount: 0,
        unusedAvailableMortgageAmount: 0,
        newMortgagesTotalAmount: 0,
        currentMortgagesTotalCurrentAmount: 0,
        currentMortgagesTotalDeposit: 0,
        currentMortgagesTotalNewAmount: 0,
    };

    protected errors = {
        mortgageRatios: {
            hasError: false,
            message: '',
        },
        mortgageAmounts: {
            hasError: false,
            message: '',
        },
    };

    constructor(
        private formBuilder: FormBuilder,
        private route: ActivatedRoute,
        private mortgageEligibilityInfoService: MortgageEligibilityInfoService,
        private fuseMediaWatcherService: FuseMediaWatcherService,
        public dialog: MatDialog,
        private translocoLocaleService: TranslocoLocaleWrapperService,
        private translationService: TranslationService,
        private cdr: ChangeDetectorRef
    ) {
        this.currentMortgagesArray = this.formBuilder.array([]);
        this.newMortgagesArray = this.formBuilder.array([]);
        this.properties = this.formBuilder.array([]);
        this.form.valueChanges.subscribe(this.calculateInfo);
    }

    ngOnInit(): void {
        this.config = this.route.snapshot.data.config;
        this.info.mortgageLimits.maxLimit.amount = this.config.maxMortgageAmount;

        const id = this.route.snapshot.paramMap.get('id');
        if (id) {
            this.fetchMortgageElgibilityInfo(parseInt(id));
        }
    }

    ngAfterViewInit(): void {
        // Subscribe to media changes
        this.fuseMediaWatcherService.onMediaChange$
            .pipe(
                filter((e) => this.response != null),
                takeUntil(this.unsubscribeAll)
            )
            .subscribe(({ matchingAliases }) => {
                // Set the drawerMode and drawerOpened
                if (matchingAliases.includes('xl')) {
                    this.infoPanelMode = 'side';
                    this.infoPanelOpen = true;
                } else {
                    this.infoPanelMode = 'over';
                    this.infoPanelOpen = false;
                }

                // Mark for check
                this.cdr.markForCheck();
            });
    }

    ngOnDestroy() {
        this.unsubscribeAll.next(null);
        this.unsubscribeAll.complete();
    }

    fetchMortgageElgibilityInfo = (id: number) => {
        this.mortgageEligibilityInfoService.getMortgageEligibilityInfo(id).subscribe((res: ExposedReadMortgageEligibilityInfoRes) => {
            this.populateForm(res, false);
            this.refreshFromOriginatingSystemIfNeeded();
        });
    };

    onAddProperty = () => {
        const dialogRef = this.dialog.open(DialogMortgageEligibilityAddPropertyComponent, {
            autoFocus: false,
            restoreFocus: false,
            width: '650px',
            disableClose: true,
            data: {
                mortgageEligibilityInfoId: this.response.id,
            } as DialogMortgageEligibilityAddPropertyData,
        });

        dialogRef.afterClosed().subscribe((res: number) => {
            if (!res) {
                return;
            }
            this.fetchMortgageElgibilityInfo(this.response.id);
        });
    };

    onRemoveProperty = (item: FormGroup) => {
        const dialogRef = this.dialog.open(DialogConfirm, {
            autoFocus: false,
            restoreFocus: false,
            width: '650px',
            disableClose: true,
            data: {
                title: this.translationService.translate('label_confirm_action'),
                message: this.translationService.translate('text_confirm_delete_mortgage_eligibility_info_property'),
            } as DialogConfirmData,
        });

        dialogRef.afterClosed().subscribe((res: boolean) => {
            if (!res) {
                return;
            }
            this.mortgageEligibilityInfoService.deleteMortgageEligibilityInfoProperty(this.response.id, item.get('id').value).subscribe({
                next: (res) => {
                    this.fetchMortgageElgibilityInfo(this.response.id);
                },
            });
        });
    };

    addPropertyRow = (property: ExposedListMortgageEligibilityInfoPropertiesRes) => {
        let newProperty = this.formBuilder.group({
            id: [property.id],
            propertyNumber: [property.propertyNumber, [ValidatePropertyNumber]],
            propertyAddress: this.formBuilder.group({
                address: [property.address],
                city: [property.city],
                postalCode: [property.postalCode],
            }),
            propertyEvaluation: [this.translocoLocaleService.localizeNumber(property.propertyEvaluation)],
            landEvaluation: [this.translocoLocaleService.localizeNumber(property.landEvaluation)],
            fireCompensationEvaluation: [this.translocoLocaleService.localizeNumber(property.fireCompensationEvaluation)],
        });

        this.properties = this.formBuilder.array([...this.properties.controls, newProperty]);
    };

    onAddCurrentMortgageRow = (lender?: string, balance?: number, deposit?: number) => {
        let morttgageLender = lender ?? 'lsr';
        let mortgageBalance = balance ? this.translocoLocaleService.localizeNumber(balance) : '';
        let mortgageDeposit = deposit ? this.translocoLocaleService.localizeNumber(deposit) : '';

        let newCurrentMortgage = this.formBuilder.group({
            mortgageLender: [morttgageLender],
            mortgageBalance: [mortgageBalance],
            mortgageDeposit: [mortgageDeposit],
        });
        this.currentMortgagesArray = this.formBuilder.array([...this.currentMortgagesArray.controls, newCurrentMortgage]);
    };

    onRemoveCurrentMortgageRow = (element: FormGroup) => {
        let index = this.currentMortgagesArray.controls.indexOf(element);
        this.currentMortgagesArray.controls.splice(index, 1);
        this.currentMortgagesArray = this.formBuilder.array([...this.currentMortgagesArray.controls]);
        this.calculateInfo();
    };

    onMoveCurrentMortgageRow = (event: FormArrayTableRowMoveEvent) => {
        let index = this.currentMortgagesArray.controls.indexOf(event.element);

        let newIndex = event.direction == 'up' ? index - 1 : index + 1;

        const reorderedArray = [...this.currentMortgagesArray.controls];

        reorderedArray[index] = reorderedArray[newIndex];
        reorderedArray[newIndex] = event.element;

        this.currentMortgagesArray = this.formBuilder.array(reorderedArray);
    };

    getNewMortgageBalance = (element: FormGroup) => {
        let currentBalance = this.translocoLocaleService.parseLocalizedNumber(element.get('mortgageBalance').value);
        let deposit = this.translocoLocaleService.parseLocalizedNumber(element.get('mortgageDeposit').value);

        return currentBalance - deposit;
    };

    onAddNewMortgageRow = (type?: string, balance?: number) => {
        let mortgageType = type ?? 'new';
        let mortgageBalance = balance ? this.translocoLocaleService.localizeNumber(balance) : '';

        let newMortgage = this.formBuilder.group({
            mortgageType: [mortgageType],
            mortgageBalance: [mortgageBalance],
        });
        this.newMortgagesArray = this.formBuilder.array([...this.newMortgagesArray.controls, newMortgage]);
    };

    onRemoveNewMortgageRow = (element: FormGroup) => {
        let index = this.newMortgagesArray.controls.indexOf(element);
        this.newMortgagesArray.controls.splice(index, 1);
        this.newMortgagesArray = this.formBuilder.array([...this.newMortgagesArray.controls]);
        this.calculateInfo();
    };

    onMoveNewMortgageRow = (event: FormArrayTableRowMoveEvent) => {
        let index = this.newMortgagesArray.controls.indexOf(event.element);

        let newIndex = event.direction == 'up' ? index - 1 : index + 1;

        const reorderedArray = [...this.newMortgagesArray.controls];

        reorderedArray[index] = reorderedArray[newIndex];
        reorderedArray[newIndex] = event.element;

        this.newMortgagesArray = this.formBuilder.array(reorderedArray);
    };

    getPropertyAddressString = (element: FormGroup) => {
        let group = element.get('propertyAddress').value;
        return formatAddress(group.address, group.city, group.postalCode);
    };

    onRefetchPropertyInfo = (element: FormGroup) => {
        let id = element.get('id').value;
        let property = this.response.properties.find((p) => p.id == id);

        if (property == null) {
            return;
        }

        const dialogRef = this.dialog.open(DialogMortgageEligibilityConfirmFetchPropertyInfoComponent, {
            autoFocus: false,
            restoreFocus: false,
            width: '650px',
            disableClose: true,
            data: {
                propertyEncumbranceInfoLastFetchedAt: property?.propertyEncumbranceInfoDocument?.timeStamp ?? null,
                propertyInfoLastFetchedAt: property?.propertyInfoDocument?.timeStamp ?? null,
            } as DialogMortgageEligibilityConfirmFetchPropertyInfoData,
        });

        dialogRef.afterClosed().subscribe((data: ExposedUpdateMortgageEligibilityPropertyInfoReq) => {
            if (!data) {
                return;
            }
            this.mortgageEligibilityInfoService
                .updateMortgageEligibilityInfoPropertyInfoWithRegistryData(this.response.id, id, data)
                .subscribe({
                    next: (res: number) => {
                        this.fetchMortgageElgibilityInfo(this.response.id);
                    },
                });
        });
    };

    onMissingOriginatingSystemApplication = () => {
        const dialogRef = this.dialog.open(DialogMortgageEligibilityMissingOriginatingApplicationComponent, {
            autoFocus: false,
            restoreFocus: false,
            width: '450px',
            disableClose: true,
            data: {
                id: this.response.id,
                externalServiceId: this.response.externalServiceId,
                originatingSystem: this.response.originatingSystem,
            } as DialogMortgageEligibilityMissingOriginatingApplicationData,
        });

        dialogRef.afterClosed().subscribe(() => {
            this.fetchMortgageElgibilityInfo(this.response.id);
        });
    };

    // Trigger dialog to import mortgage eligibility info from another mortgage eligibility info
    onCopyDataFromOtherMortgageEligibilityInfo = () => {
        const dialogRef = this.dialog.open(DialogMortgageEligibilityCopyDataComponent, {
            autoFocus: false,
            restoreFocus: false,
            width: '500px',
            disableClose: true,
            data: {
                id: this.response.id,
            } as DialogMortgageEligibilityCopyDataData,
        });

        dialogRef.afterClosed().subscribe((shouldRefresh) => {
            if (shouldRefresh) {
                this.fetchMortgageElgibilityInfo(this.response.id);
            }
        });
    };

    // Calculates additional information that gets displayed in the UI
    calculateInfo = () => {
        // Set evaluation totals
        this.info.evaluationTotals.propertyEvaluation = 0;
        this.info.evaluationTotals.landEvaluation = 0;
        this.info.evaluationTotals.fireCompensationEvaluation = 0;
        for (let property of this.properties.controls) {
            this.info.evaluationTotals.propertyEvaluation += this.translocoLocaleService.parseLocalizedNumber(
                property.get('propertyEvaluation').value
            );
            this.info.evaluationTotals.landEvaluation += this.translocoLocaleService.parseLocalizedNumber(
                property.get('landEvaluation').value
            );
            this.info.evaluationTotals.fireCompensationEvaluation += this.translocoLocaleService.parseLocalizedNumber(
                property.get('fireCompensationEvaluation').value
            );
        }

        // Calculate total mortgage amounts
        let totalLSRMortgagesAmount = 0;
        let totalOtherMortgagesAmount = 0;
        let totalCurrentMortgagesDeposit = 0;
        let totalCurrentMortgagesBalance = 0;
        for (let control of this.currentMortgagesArray.controls) {
            totalCurrentMortgagesDeposit += this.translocoLocaleService.parseLocalizedNumber(control.value.mortgageDeposit);
            totalCurrentMortgagesBalance += this.translocoLocaleService.parseLocalizedNumber(control.value.mortgageBalance);
            let mortgageNewBalance = this.getNewMortgageBalance(control as FormGroup);
            if (control.value.mortgageLender == 'lsr') {
                totalLSRMortgagesAmount += mortgageNewBalance;
            } else {
                totalOtherMortgagesAmount += mortgageNewBalance;
            }
        }
        this.info.currentMortgagesTotalCurrentAmount = totalCurrentMortgagesBalance;
        this.info.currentMortgagesTotalDeposit = totalCurrentMortgagesDeposit;
        this.info.currentMortgagesTotalNewAmount = totalLSRMortgagesAmount + totalOtherMortgagesAmount;

        // Store the total amount for current mortgages
        let currentMorgagesAmounts = {
            lsr: totalLSRMortgagesAmount,
            other: totalOtherMortgagesAmount,
            total: totalLSRMortgagesAmount + totalOtherMortgagesAmount,
        };

        var totalNewLSRMortgagesAmount = 0;
        for (let control of this.newMortgagesArray.controls) {
            let mortgage = control.value;
            totalNewLSRMortgagesAmount += this.translocoLocaleService.parseLocalizedNumber(mortgage.mortgageBalance);
        }

        this.info.newMortgagesTotalAmount = totalNewLSRMortgagesAmount;
        totalLSRMortgagesAmount += totalNewLSRMortgagesAmount;

        this.info.mortgageAmounts.lsr = totalLSRMortgagesAmount;
        this.info.mortgageAmounts.other = totalOtherMortgagesAmount;
        this.info.mortgageAmounts.total = totalLSRMortgagesAmount + totalOtherMortgagesAmount;

        // Max limit for LSR loans is reduced by other mortgages that precede the new loans priority
        this.info.mortgageLimits.maxLimit.amount = this.config.maxMortgageAmount - currentMorgagesAmounts.other;

        // Get property value to use in calculation (purchase price or property evaluation)
        let purchasePrice = this.translocoLocaleService.parseLocalizedNumber(this.form.get('purchasePrice').value);
        let propertyEvaluation = this.info.evaluationTotals.propertyEvaluation;

        let propertyValue = 0;
        if (purchasePrice > 0) {
            propertyValue = purchasePrice;
            this.info.mortgageLimits.onlyLSRLoans.limitBasis = 'purchase_price';
            this.info.mortgageLimits.withOtherLoans.limitBasis = 'purchase_price';
        } else {
            propertyValue = propertyEvaluation;
            this.info.mortgageLimits.onlyLSRLoans.limitBasis = 'property_evaluation';
            this.info.mortgageLimits.withOtherLoans.limitBasis = 'property_evaluation';
        }

        // Only calculate if the property value is more than 0
        if (propertyValue > 0) {
            // Calculate mortgage ratios
            this.calculateMortgageRatios(propertyValue, totalLSRMortgagesAmount, totalOtherMortgagesAmount);

            let hasNonLSRMortgages =
                this.currentMortgagesArray.controls.find(
                    (control) =>
                        control.get('mortgageLender').value == 'other' &&
                        this.translocoLocaleService.parseLocalizedNumber(control.get('mortgageBalance').value) >
                            this.translocoLocaleService.parseLocalizedNumber(control.get('mortgageDeposit').value)
                ) != null;

            // Calculate the max mortgage amount available
            let maxAvailableMortgageAmount = this.calculateMortgageLimits(propertyValue, hasNonLSRMortgages, currentMorgagesAmounts);

            let maxMortgageRatio = hasNonLSRMortgages
                ? this.config.mortgageRatioLimits.withOtherLoans
                : this.config.mortgageRatioLimits.onlyLSRLoans;

            this.info.maxAvailableMortgageAmount = maxAvailableMortgageAmount - currentMorgagesAmounts.total;
            this.info.unusedAvailableMortgageAmount = maxAvailableMortgageAmount - this.info.mortgageAmounts.total;

            // Validate form

            if (this.info.mortgageRatios.total > maxMortgageRatio) {
                this.errors.mortgageRatios = {
                    hasError: true,
                    message: 'Veðsetning of há',
                };
            } else {
                this.errors.mortgageRatios = {
                    hasError: false,
                    message: '',
                };
            }

            if (this.info.mortgageAmounts.lsr > maxAvailableMortgageAmount) {
                this.errors.mortgageAmounts = {
                    hasError: true,
                    message: 'Lánsupphæð LSR lána of há',
                };
            } else {
                this.errors.mortgageAmounts = {
                    hasError: false,
                    message: '',
                };
            }
        }
    };

    calculateMortgageRatios = (propertyValue: number, totalLSRMortgageAmount: number, totalOtherMortgageAmount: number) => {
        this.info.mortgageRatios.lsr = totalLSRMortgageAmount / propertyValue;
        this.info.mortgageRatios.other = totalOtherMortgageAmount / propertyValue;
        this.info.mortgageRatios.total = this.info.mortgageRatios.lsr + this.info.mortgageRatios.other;
    };

    // Calculates the mortgagelimits and returns the active limit amount
    calculateMortgageLimits = (
        propertyValue: number,
        hasNonLSRMortgages: boolean,
        currentMortgagesAmount: { lsr: number; other: number; total: number }
    ): number => {
        let landEvaluation = this.info.evaluationTotals.landEvaluation;
        let fireCompensationEvaluation = this.info.evaluationTotals.fireCompensationEvaluation;
        let {
            onlyLSRLoans: onlyLSRLoansLimit,
            withOtherLoans: withOtherLoansLimit,
            fireCompensationAndLandEvaluation: fireCompensationAndLandEvaluationLimit,
            maxLimit,
        } = this.info.mortgageLimits;

        // Determine mortgage limits
        onlyLSRLoansLimit.amount = Math.round(Math.max(propertyValue * this.config.mortgageRatioLimits.onlyLSRLoans, 0));
        withOtherLoansLimit.amount = Math.round(Math.max(propertyValue * this.config.mortgageRatioLimits.withOtherLoans, 0));
        fireCompensationAndLandEvaluationLimit.amount = Math.round(
            Math.max((landEvaluation + fireCompensationEvaluation) * this.config.mortgageRatioLimits.fireCompensationAndLandEvaluation, 0)
        );

        // Determine which mortgage limit should be active
        onlyLSRLoansLimit.active =
            !hasNonLSRMortgages &&
            (fireCompensationAndLandEvaluationLimit.amount == 0 ||
                onlyLSRLoansLimit.amount <= fireCompensationAndLandEvaluationLimit.amount);
        withOtherLoansLimit.active =
            hasNonLSRMortgages &&
            (fireCompensationAndLandEvaluationLimit.amount == 0 ||
                withOtherLoansLimit.amount <= fireCompensationAndLandEvaluationLimit.amount);
        fireCompensationAndLandEvaluationLimit.active =
            fireCompensationAndLandEvaluationLimit.amount == 0
                ? false
                : hasNonLSRMortgages
                  ? fireCompensationAndLandEvaluationLimit.amount < withOtherLoansLimit.amount
                  : fireCompensationAndLandEvaluationLimit.amount < onlyLSRLoansLimit.amount;

        var mortgageLimit = 0;

        // Return the active limit amount
        if (onlyLSRLoansLimit.active) {
            mortgageLimit = onlyLSRLoansLimit.amount;
        } else if (withOtherLoansLimit.active) {
            mortgageLimit = withOtherLoansLimit.amount;
        } else if (fireCompensationAndLandEvaluationLimit.active) {
            mortgageLimit = fireCompensationAndLandEvaluationLimit.amount;
        }

        var diffTotalCurrentAndLimit = currentMortgagesAmount.total - mortgageLimit;
        var diffLSRCurrentAndMaxLimit = currentMortgagesAmount.lsr - maxLimit.amount;

        if (diffTotalCurrentAndLimit < diffLSRCurrentAndMaxLimit) {
            onlyLSRLoansLimit.active = false;
            withOtherLoansLimit.active = false;
            fireCompensationAndLandEvaluationLimit.active = false;

            maxLimit.active = true;
            return maxLimit.amount + currentMortgagesAmount.other;
        } else {
            maxLimit.active = false;
            return mortgageLimit;
        }
    };

    private markFormAsPristine(): void {
        this.form.markAsPristine();
        this.currentMortgagesArray.markAsPristine();
        this.newMortgagesArray.markAsPristine();
        this.properties.markAsPristine();
    }

    private hasUnsavedChanges(): boolean {
        return this.form.dirty || this.currentMortgagesArray.dirty || this.newMortgagesArray.dirty || this.properties.dirty;
    }

    private populateForm(res: ExposedReadMortgageEligibilityInfoRes, checkMissingApplication = true): void {
        this.response = res;

        this.form.patchValue({
            externalServiceId: res.externalServiceId,
            externalServiceLink: res.externalServiceLink,
            purchasePrice: this.translocoLocaleService.localizeNumber(res.purchasePrice),
        });

        this.properties.clear();
        this.currentMortgagesArray.clear();
        this.newMortgagesArray.clear();

        for (let p of res.properties ?? []) {
            this.addPropertyRow(p);
        }

        for (let mortgage of res.currentMortgages ?? []) {
            this.onAddCurrentMortgageRow(mortgage.mortgageLender, mortgage.mortgageBalance, mortgage.mortgageDeposit);
        }

        for (let mortgage of res.newMortgages ?? []) {
            this.onAddNewMortgageRow(mortgage.mortgageType, mortgage.mortgageBalance);
        }

        this.calculateInfo();
        this.markFormAsPristine();

        if (checkMissingApplication && !this.response.originatingSystemApplicationId) {
            this.onMissingOriginatingSystemApplication();
        }
    }

    private buildUpdateRequest(): ExposedUpdateMortgageEligibilityInfoReq {
        const request = {
            mortgageEligibilityInfo: {
                id: this.response.id,
                purchasePrice: this.translocoLocaleService.parseLocalizedNumber(this.form.get('purchasePrice').value),
            },
            currentMortgages: [],
            newMortgages: [],
            properties: [],
        } as ExposedUpdateMortgageEligibilityInfoReq;

        this.currentMortgagesArray.controls.forEach((control) => {
            request.currentMortgages.push({
                mortgageBalance: this.translocoLocaleService.parseLocalizedNumber(control.get('mortgageBalance').value),
                mortgageDeposit: this.translocoLocaleService.parseLocalizedNumber(control.get('mortgageDeposit').value),
                mortgageLender: control.get('mortgageLender').value,
            });
        });

        this.newMortgagesArray.controls.forEach((control) => {
            request.newMortgages.push({
                mortgageBalance: this.translocoLocaleService.parseLocalizedNumber(control.get('mortgageBalance').value),
                mortgageType: control.get('mortgageType').value,
            });
        });

        this.properties.controls.forEach((control) => {
            request.properties.push({
                id: control.get('id').value,
                propertyNumber: control.get('propertyNumber').value,
                address: control.get('propertyAddress.address').value,
                city: control.get('propertyAddress.city').value,
                postalCode: control.get('propertyAddress.postalCode').value,
                landEvaluation: this.translocoLocaleService.parseLocalizedNumber(control.get('landEvaluation').value),
                propertyEvaluation: this.translocoLocaleService.parseLocalizedNumber(control.get('propertyEvaluation').value),
                fireCompensationEvaluation: this.translocoLocaleService.parseLocalizedNumber(
                    control.get('fireCompensationEvaluation').value
                ),
            });
        });

        return request;
    }

    private refreshFromOriginatingSystemIfNeeded(): void {
        if (this.response?.originatingSystem !== ORIGINATING_SYSTEMS.Libra) {
            return;
        }

        if (this.hasUnsavedChanges() || this.isCheckingOriginatingSystem) {
            return;
        }

        const mortgageEligibilityInfoId = this.response.id;
        this.isCheckingOriginatingSystem = true;

        this.mortgageEligibilityInfoService
            .getOriginatingSystemComparison(mortgageEligibilityInfoId)
            .pipe(
                switchMap((comparison) => {
                    if (!comparison.canRefresh || !comparison.mismatches?.length) {
                        return EMPTY;
                    }

                    if (this.hasUnsavedChanges()) {
                        return EMPTY;
                    }

                    return this.confirmOriginatingSystemRefresh(comparison.mismatches).pipe(
                        filter((confirmed) => confirmed),
                        filter(() => !this.hasUnsavedChanges() && this.response?.id === mortgageEligibilityInfoId),
                        switchMap(() =>
                            this.mortgageEligibilityInfoService.refreshMortgageEligibilityInfoFromOriginatingSystem(
                                mortgageEligibilityInfoId
                            )
                        )
                    );
                }),
                finalize(() => {
                    this.isCheckingOriginatingSystem = false;
                })
            )
            .subscribe((res) => {
                if (!res?.updated || !res.mortgageEligibilityInfo) {
                    return;
                }

                this.populateForm(res.mortgageEligibilityInfo);
            });
    }

    private confirmOriginatingSystemRefresh(mismatches: ExposedMortgageEligibilityInfoOriginatingSystemMismatchRes[]): Observable<boolean> {
        const mismatchText = this.buildOriginatingSystemMismatchMessage(mismatches);

        const dialogRef = this.dialog.open(DialogConfirm, {
            width: '600px',
            maxWidth: '95vw',
            data: {
                title: this.translationService.translate('label_mortgage_eligibility_mismatch_found'),
                message: [
                    this.translationService.translate('text_mortgage_eligibility_mismatch_found'),
                    mismatchText,
                    this.translationService.translate('text_confirm_mortgage_eligibility_refresh'),
                ]
                    .filter(Boolean)
                    .join('\n\n'),
                confirmLabel: this.translationService.translate('label_update_mortgage_eligibility'),
                cancelLabel: this.translationService.translate('label_cancel'),
            },
        });

        return dialogRef.afterClosed().pipe(map((result) => result === true));
    }

    private buildOriginatingSystemMismatchMessage(mismatches: ExposedMortgageEligibilityInfoOriginatingSystemMismatchRes[]): string {
        if (!mismatches?.length) {
            return '';
        }

        const mismatchRows = mismatches.map((m, index) => {
            const valueType = m.valueType ?? 'text';

            const currentValue = this.formatOriginatingSystemMismatchValue(m.currentValue, valueType);
            const originatingSystemValue = this.formatOriginatingSystemMismatchValue(m.originatingSystemValue, valueType);

            return [`${index + 1}. ${m.label}`, `   Veðhæfisskjal: ${currentValue}`, `   Libra: ${originatingSystemValue}`].join('\n');
        });

        return ['Mismunur sem fannst:', ...mismatchRows].join('\n\n');
    }

    private formatOriginatingSystemMismatchValue(value?: string, valueType: 'currency' | 'number' | 'text' = 'text'): string {
        if (value == null || value === '') {
            return '-';
        }

        const numericValue = Number(value);

        if ((valueType === 'currency' || valueType === 'number') && !Number.isNaN(numericValue)) {
            const formattedNumber = this.translocoLocaleService.localizeNumber(numericValue);

            return valueType === 'currency' ? `${formattedNumber} kr.` : formattedNumber;
        }

        return value;
    }

    private saveMortgageEligibilityInfo(): Observable<void> {
        const request = this.buildUpdateRequest();

        return this.mortgageEligibilityInfoService.updateMortgageEligibilityInfo(request).pipe(
            tap(() => this.markFormAsPristine()),
            map(() => void 0)
        );
    }

    onSave = () => {
        this.saveMortgageEligibilityInfo().subscribe();
    };

    toggleInfoPanel = () => {
        this.infoPanelOpen = !this.infoPanelOpen;
    };

    saveChangesBeforeLeave = (): Observable<boolean> => {
        return this.saveMortgageEligibilityInfo().pipe(
            map(() => true),
            catchError(() => of(false))
        );
    };

    @HostListener('window:beforeunload')
    canDeactivate = (): boolean => {
        return !this.hasUnsavedChanges();
    };
}
